import { z } from "zod";

/**
 * Cross-cutting zod schemas. Validators live at boundaries; the inferred
 * types flow through the app. Domain-specific extensions can re-import
 * and `.extend()` these.
 */

export const PrefixedUlid = z
  .string()
  .regex(/^[a-z]{2,6}_[0-9A-HJKMNP-TV-Z]{26}$/, "expected prefixed ULID");

export const Iso4217 = z.string().regex(/^[A-Z]{3}$/, "expected ISO 4217 currency");

export const YyyymmddLocalSchema = z
  .string()
  .regex(/^\d{8}$/, "expected YYYYMMDD local date");

export const CentsSchema = z.number().int().nonnegative();
export const UtcMsSchema = z.number().int().nonnegative();

export const BriefStyle = z.enum([
  "classic",
  "modern",
  "rustic",
  "boho",
  "garden",
  "industrial",
  "luxury",
  "minimal",
  "vintage",
  "tropical",
]);

/**
 * Photography "Look" — the photographic treatment the couple picks in the
 * builder's Look step. Unlike `BriefStyle` (the décor aesthetic), this drives
 * the *camera/grade* of every generated image: it threads into the per-package
 * `StyleSheet.photography` and into the `vision.scene` prompt so the whole
 * carousel is shot in one consistent style. Kept a small closed set so the
 * builder chip picker and the `deriveStyleSheet` treatment map stay in lockstep.
 */
export const PhotographyStyle = z.enum([
  "editorial",
  "fine_art",
  "light_airy",
  "moody",
  "documentary",
  "romantic",
]);
export type PhotographyStyle = z.infer<typeof PhotographyStyle>;

/**
 * Food service style — the builder's Food step. `buffet` covers both classic
 * buffets and chef-attended food stations (stations are a buffet-style option,
 * narrowed client-side via the references `dining` filter, not persisted).
 */
export const FoodService = z.enum(["buffet", "sit_down"]);
export type FoodService = z.infer<typeof FoodService>;

/**
 * Course vocabulary — mirrors the catering pack's `vendor_item.attributes.course`
 * enum so item-linked images classify losslessly. (`drink` is deliberately
 * excluded: it isn't a plated course the builder shows.)
 */
export const CourseKey = z.enum(["hors_doeuvre", "appetizer", "entree", "side", "dessert"]);
export type CourseKey = z.infer<typeof CourseKey>;

/**
 * Course plan per sit-down course COUNT (3–5, matching the `vision.menu`
 * prompt's "3–5 course" rule). Single source of truth for the builder's course
 * tabs, the references `course=` validation, and the menu prompt — exported so
 * web + API can never disagree on what "a 4-course dinner" contains.
 */
export const COURSE_SETS: Record<3 | 4 | 5, readonly CourseKey[]> = {
  3: ["appetizer", "entree", "dessert"],
  4: ["hors_doeuvre", "appetizer", "entree", "dessert"],
  5: ["hors_doeuvre", "appetizer", "entree", "side", "dessert"],
};

export const Palette = z.array(z.string().regex(/^#[0-9A-Fa-f]{6}$/)).max(8);

/**
 * Wedding-identity "lookbook" derived once per package from the brief +
 * matched anchor vendors (venue + florist). Injected verbatim into every
 * scene prompt so the carousel reads as one event, not five disconnected
 * shoots. Stored in `package.style_sheet_json`.
 */
export const StyleSheet = z.object({
  palette: z.array(z.string()).max(8),       // hex or named colors
  materials: z.array(z.string()).max(8),     // "linen", "raw wood", "soft brass"
  lighting: z.string(),                       // "late-afternoon golden hour, soft natural"
  formality: z.string(),                      // "garden-romantic, semi-formal"
  season_hint: z.string(),                    // "late spring"
  mood: z.string(),                           // "intimate, candlelit, joyful"
  // The couple's photographic "Look" → the camera/grade applied to every scene.
  // Optional so existing persisted style sheets (no photography key) still parse.
  photography: z.string().optional(),         // "candid documentary photojournalism, natural light"
  // The bride's hair + makeup look — held IDENTICAL across every scene she
  // appears in (the consistency goal). Heuristic per style, overridden by a
  // descriptor distilled from the couple's picked hair/makeup reference images.
  // Optional so existing persisted style sheets still parse.
  hair: z.string().optional(),                // "soft romantic waves swept to one side, loose and airy"
  makeup: z.string().optional(),              // "fresh romantic — dewy skin, soft rosy cheeks, a soft pink lip"
  // The groom's suit look — held IDENTICAL in every picture he appears in (the
  // consistency goal), the male counterpart of hair/makeup. Heuristic per style,
  // overridden by a descriptor distilled from the couple's picked groom-attire
  // reference image (so the suit reflects WHAT THEY PICKED — the styling-not-the-
  // person text backstop the groom otherwise lacks). Optional so existing
  // persisted style sheets still parse.
  groom: z.string().optional(),               // "a tailored navy three-piece suit, peak lapel, white shirt, burgundy tie"
});
export type StyleSheet = z.infer<typeof StyleSheet>;

export const Brief = z.object({
  area: z.string().min(1).optional(),
  date_local: YyyymmddLocalSchema.optional(),
  season: z.enum(["spring", "summer", "fall", "winter"]).optional(),
  guests: z.number().int().positive().optional(),
  budget_cents: CentsSchema.optional(),
  currency: Iso4217.optional(),
  styles: z.array(BriefStyle).max(4).optional(),
  palette: Palette.optional(),
  categories: z.array(z.string()).optional(),
  notes: z.string().max(4000).optional(),
  // The builder's Look step → the photographic treatment for generation.
  photography_style: PhotographyStyle.optional(),
  // The builder's Food step → menu service style + sit-down course count.
  // Flat keys (not a nested object) so the /plan/brief whole-key merge can
  // PATCH the service toggle and the course picker independently.
  food_service: FoodService.optional(),
  food_courses: z.number().int().min(3).max(5).optional(),
  // Per-course dishes the couple authored at the Food step (typed, or pre-filled
  // from a picked reference). A FLAT array — the /plan/brief whole-key merge
  // PATCHes it independently of food_service/food_courses. Each entry becomes one
  // generated MENU image (§D, kill switch vision.menu_dish_images_enabled); the
  // service style (sit_down vs buffet) drives plated-vs-display framing. Capped
  // so the per-dish fan-out stays within the scene budget.
  menu_dishes: z.array(z.object({
    course: CourseKey,
    dish: z.string().min(1).max(120),
  })).max(6).optional(),
});
export type Brief = z.infer<typeof Brief>;

/**
 * One published offering on a vendor's site — a "Gold Package", a cake
 * tier, a menu item. `price_cents` is nullable because many vendors don't
 * publish prices ("contact for quote"); we explicitly distinguish that
 * from "actually $0".
 */
export const FlowerCategory = z.enum([
  "bouquet",
  "corsage",
  "boutonniere",
  "vases",
  "arrangement",
  "centerpiece",
  "candles",
  "cake_flowers",
]);
export type FlowerCategory = z.infer<typeof FlowerCategory>;

export const FlowerMaterial = z.enum(["fresh", "dried", "artificial"]);
export type FlowerMaterial = z.infer<typeof FlowerMaterial>;

/**
 * Cross-vendor page taxonomy. The crawl `page_type` step (LLM-driven over
 * URL paths) classifies every fetched URL into exactly one of these labels.
 * Stored verbatim on `crawl_page.page_type` and `FetchedPage.page_type`.
 * A subset — `portfolio` / `venue_spaces` / `products` / `testimonials` —
 * propagates onto `vendor_image.image_type` via the imagePageTypeByUrl
 * sidecar (other types aren't useful for image typing).
 */
export const PageType = z.enum([
  "home",
  "about",
  "contact",
  "services",
  "pricing",
  "products",
  "portfolio",
  "testimonials",
  "faq",
  "blog",
  "preferred_vendors",
  "venue_spaces",
  "legal",
  "other",
]);
export type PageType = z.infer<typeof PageType>;

export const VendorItemDraft = z.object({
  title: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  category: z.string().optional(),                                  // matched against vendor.category at insert
  price_cents: z.number().int().nonnegative().nullable().optional(),
  currency: Iso4217.optional(),
  // Populated by the florist-only `flower_classify` step; null when the model
  // had no in-text signal. Persisted to vendor_item.flower_material.
  flower_material: FlowerMaterial.nullable().optional(),
  // AEVS grounding (extract prompt v4): verbatim quote from the corpus that
  // supports this item. Verified post-extract; items whose source_span isn't
  // found in the corpus are dropped. Not persisted — pipeline-internal only.
  source_span: z.string().max(500).optional(),
  // Extract prompt v8: ids of `[IMG-N]` placeholders the LLM associated with
  // this item. The pipeline whitelists these against the page's legend and
  // resolves them to absolute URLs in extract.ts before mergeDrafts. Optional
  // because pages without the legend (browser-rendering branch + flag-off)
  // emit none. Capped at 6 in the prompt + schema as defense-in-depth.
  image_refs: z.array(z.string().regex(/^IMG-[0-9]+$/)).max(6).optional(),
});
export type VendorItemDraft = z.infer<typeof VendorItemDraft>;

/**
 * Per-page intermediate of `VendorDraft` — every field optional because any
 * given page (a /pricing page, an /about page) only carries a slice of the
 * full vendor profile. The crawl extract step fans out per page, validates
 * each response against this relaxed shape, then deterministically merges
 * the partials into a strict `VendorDraft`.
 */
export const VendorDraftPartial = z.object({
  source_url: z.string().url().optional(),
  name: z.string().optional(),
  categories: z.array(z.string()).optional(),
  description: z.string().optional(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  address_text: z.string().optional(),
  city: z.string().optional(),
  region: z.string().optional(),
  country: z.string().length(2).optional(),
  postal_code: z.string().optional(),
  image_urls: z.array(z.string().url()).optional(),
  items: z.array(VendorItemDraft).max(100).optional(),
  social_links: z.record(z.string().url()).optional(),
  confidence: z.record(z.number().min(0).max(1)).optional(),
});
export type VendorDraftPartial = z.infer<typeof VendorDraftPartial>;

export const VendorDraft = z.object({
  source_url: z.string().url(),
  name: z.string().min(1),
  categories: z.array(z.string()).min(1),
  description: z.string().optional(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  address_text: z.string().optional(),
  city: z.string().optional(),
  region: z.string().optional(),
  country: z.string().length(2).optional(),
  postal_code: z.string().optional(),
  image_urls: z.array(z.string().url()).default([]),
  items: z.array(VendorItemDraft).max(100).optional(),
  social_links: z.record(z.string().url()).optional(),
  confidence: z.record(z.number().min(0).max(1)).optional(),
});
export type VendorDraft = z.infer<typeof VendorDraft>;

export const MomentSpec = z.object({
  key: z.enum([
    "first_look",
    "ceremony",
    "florals",
    "tablescape",
    "feast",
    "cake",
    "send_off",
  ]),
  title: z.string(),
  prompt_seed: z.string(),
  required_categories: z.array(z.string()),
  aspect: z.enum(["16:9", "4:5", "1:1", "3:2"]).default("16:9"),
});
export type MomentSpec = z.infer<typeof MomentSpec>;

// ── Wedding preview cost breakdown (GET /packages/:id/breakdown) ─────────────
// One cost per recommended vendor. `low_cents`/`high_cents` are always the
// WEDDING-LEVEL total (per-head categories already × guests) so the budget tally
// can sum midpoints directly; `per_guest_*` carry the per-plate figure for the
// "$X/guest × N" caption. Three kinds: a published vendor_item rollup, a
// web-search+LLM estimate, or a coarse price_band fallback.
const CostBasis = z.enum(["flat", "per_head"]);
export const CategoryCost = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("published"),
    low_cents: z.number().int(), high_cents: z.number().int(),
    basis: CostBasis,
    per_guest_low_cents: z.number().int().nullable().optional(),
    per_guest_high_cents: z.number().int().nullable().optional(),
  }),
  z.object({
    kind: z.literal("estimated"),
    low_cents: z.number().int(), high_cents: z.number().int(),
    basis: CostBasis,
    per_guest_low_cents: z.number().int().nullable().optional(),
    per_guest_high_cents: z.number().int().nullable().optional(),
    caveat: z.string(),
    sources: z.array(z.string()).optional(),
  }),
  z.object({
    kind: z.literal("band"),
    low_cents: z.number().int(), high_cents: z.number().int(),
    band: z.number().int().nullable(),
    caveat: z.string(),
  }),
]);
export type CategoryCost = z.infer<typeof CategoryCost>;

export const VendorCost = z.object({
  category: z.string(),
  label: z.string(),
  vendor_id: z.string(),
  vendor_name: z.string(),
  vendor_description: z.string().nullable(),
  primary_image_id: z.string().nullable(),
  cost: CategoryCost,
});
export type VendorCost = z.infer<typeof VendorCost>;

export const MenuRecommendation = z.object({
  vendor_id: z.string(),
  vendor_name: z.string(),
  headline: z.string(),
  courses: z.array(z.object({ course: z.string(), dishes: z.array(z.string()) })),
  note: z.string().nullable(),
  caveat: z.string(),
});
export type MenuRecommendation = z.infer<typeof MenuRecommendation>;

export const BudgetTally = z.object({
  target_cents: z.number().int().nullable(),
  estimated_total_cents: z.number().int(),          // Σ midpoints — drives over/under
  estimated_low_cents: z.number().int(),            // Σ per-category lows
  estimated_high_cents: z.number().int(),           // Σ per-category highs
  delta_cents: z.number().int().nullable(),         // target - total (positive = under)
  over_or_under: z.enum(["over", "under", "on_target", "no_target"]),
  pct_of_target: z.number().nullable(),
});
export type BudgetTally = z.infer<typeof BudgetTally>;

export const BreakdownResponse = z.object({
  wedding_id: z.string(),
  currency: z.string(),
  guests: z.number().int().nullable(),
  vendors: z.array(VendorCost),
  menu: MenuRecommendation.nullable(),
  budget: BudgetTally,
});
export type BreakdownResponse = z.infer<typeof BreakdownResponse>;
